import { useCallback, useState } from "react";

import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router-dom";

import { useAuthContext } from "./useAuthContext";
import { isPhoneErrorCode } from "../components/Authentication/components/utils";
import { toE164 } from "../components/PhoneNumberField";
import { ClientAppEvent } from "../constants";

import type { SignupSubmissionData } from "../components/Authentication/components/SignUp/types";

// Signup failures without a backend `code` are identified by HTTP status.
// The catch-all is a 401 (no body, per the 401 no-leak convention), so anything
// unmapped is an unexpected server/network failure worth surfacing distinctly.
const STATUS_ERROR_CODES: Record<number, string> = {
  429: "RATE_LIMITED",
  503: "SIGNUP_PAUSED",
};

interface UseSignupSubmissionProps {
  executeReCaptcha: () => void;
}

interface UseSignupSubmissionReturn {
  loading: boolean;
  signupError: string | null;
  handleSignup: (signupData: SignupSubmissionData) => void;
  setSignupError: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useSignupSubmission({
  executeReCaptcha,
}: UseSignupSubmissionProps): UseSignupSubmissionReturn {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { signup } = useAuthContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [signupError, setSignupError] = useState<string | null>(null);

  const handleSignupError = useCallback(
    (error: any, signupData: SignupSubmissionData) => {
      const status = error?.response?.status;
      const errorCode =
        error?.response?.data?.code ??
        STATUS_ERROR_CODES[status] ??
        "SIGNUP_UNEXPECTED";
      // Signup is anonymous (identify() only runs on login); email lets us find
      // the failed session replay in PostHog by who hit the error. For phone
      // rejections, attach the exact values entered — replay masks inputs, so
      // this is how support can see the bad number the user actually typed.
      posthog?.capture(ClientAppEvent.SIGNUP_ERROR, {
        error_code: errorCode,
        email: signupData.email,
        persona: signupData.persona,
        ...(isPhoneErrorCode(errorCode) && {
          attempted_work_phone: signupData.workPhoneNumber,
          attempted_cell_phone: signupData.cellPhoneNumber,
          attempted_extension: signupData.workPhoneNumberExtension,
        }),
      });

      if (status === 429) {
        setSignupError(
          "Exceeded signup attempts. Please try again in 60 seconds.",
        );
        return;
      }

      if (
        error?.response?.data?.code === "RECAPTCHA_DUPE" ||
        error?.response?.data?.code === "RECAPTCHA_EXPIRED"
      ) {
        setSignupError(
          error?.response?.data?.message ??
            "There was an error while creating your account. Please contact us if the issue persists.",
        );
      } else {
        setSignupError(
          error?.response?.data?.message ??
            "There was an error while creating your account. Please contact us if the issue persists.",
        );
      }

      executeReCaptcha();
    },
    [executeReCaptcha, posthog],
  );

  const handleSignup = useCallback(
    (signupData: SignupSubmissionData) => {
      setLoading(true);
      signup(
        {
          email: signupData.email,
          first_name: signupData.firstName,
          last_name: signupData.lastName,
          recatpcha_token: signupData.recaptchaToken,
          work_phone_number: toE164(signupData.workPhoneNumber),
          work_phone_number_extension:
            signupData.workPhoneNumberExtension.trim() || undefined,
          cell_phone_number: toE164(signupData.cellPhoneNumber) || undefined,
        },
        signupData.password,
        signupData.role,
      )
        .then(() => {
          setSignupError(null);
          posthog?.capture(ClientAppEvent.SIGNUP_SUCCEEDED, {
            email: signupData.email,
            persona: signupData.persona,
          });
          navigate("/sign-up/success");
        })
        .catch((error) => handleSignupError(error, signupData))
        .finally(() => {
          setLoading(false);
        });
    },
    [signup, navigate, handleSignupError, posthog],
  );

  return {
    loading,
    signupError,
    handleSignup,
    setSignupError,
  };
}
